/**
 * Generate srcset string for responsive images
 *
 * @author Team CMS
 * @param src - The base image source URL.
 * @param requestedWidths - An array of widths requested for generating responsive variants.
 * @param maxWidth - The maximum width to consider from CMS_AVAILABLE_WIDTHS.
 * @returns A `srcset` string for the `img` tag, or `undefined` if the src is an absolute URL or SVG.
 */

import { getFilteredWidths, transformToSizedSrc } from './imageWidths.js';
import { resolveCdnUrl } from './resolveCdnUrl.js';

/**
 * Finds the closest width from the available CMS widths.
 *
 * @param targetWidth - The target width to find the closest match for.
 * @param availableWidths - An array of available widths to choose from.
 * @returns The closest width from the available widths.
 */
const findClosestWidth = (targetWidth: number, availableWidths: number[]): number => {
  return availableWidths.reduce((prev, curr) =>
    Math.abs(curr - targetWidth) < Math.abs(prev - targetWidth) ? curr : prev
  );
};

/**
 * Generates a `srcset` attribute value for responsive images, handling both
 * legacy and Picasso URL formats. Legacy URLs are transformed to Picasso
 * format with dynamic width.
 *
 * @param src - The base image source URL.
 * @param requestedWidths - An array of widths requested for generating responsive variants.
 * @param maxWidth - The maximum width to consider from CMS_AVAILABLE_WIDTHS.
 * @returns A `srcset` string for the `img` tag, or `undefined` if the src is an absolute URL or SVG.
 */
const generateSrcsetString = (
  src: string,
  requestedWidths: number[] | undefined,
  maxWidth: number
): string | undefined => {
  // Check if the source is an absolute URL (starts with 'http://' or 'https://')
  if (/^(https?:)/i.test(src)) {
    return undefined;
  }

  // Check if the source is an SVG image (ends with .svg)
  if (src.toLowerCase().endsWith('.svg')) {
    return undefined;
  }

  // Filter CMS_AVAILABLE_WIDTHS to only include widths that are <= maxWidth
  const filteredWidths = getFilteredWidths(maxWidth);

  // Use filteredWidths if requestedWidths is not provided or empty
  const effectiveWidths = requestedWidths && requestedWidths.length > 0 ? requestedWidths : filteredWidths;

  // Map the effective widths to the closest available widths
  const closestWidths = effectiveWidths.map((width) => findClosestWidth(width, filteredWidths));

  return closestWidths
    .map((width) => {
      const modifiedSrc = transformToSizedSrc(src, width) ?? src;

      // Resolve the correct host for the URL
      const resolvedUrl = resolveCdnUrl(modifiedSrc);

      return `${resolvedUrl} ${width}w`;
    })
    .join(', ');
};

export { generateSrcsetString };
